import React, { useEffect, useState, useRef } from "react";
import { OverlayPanel } from "primereact/overlaypanel";
import { KeyboardDatePicker } from "@material-ui/pickers";
import { Card, Badge } from "react-bootstrap";
import Illustration from "../../../../partials/content/Illustration";
import CustomDataTable from "../../../../partials/content/CustomDataTable";
import { getVisitSchedulesReport } from "../../../../services/crm/crmReportService";
import { formatDate } from "../../../../utils/utils";
import { useGlobalData } from "../../../../../provider/GlobalDataProvider";
import { DateTime } from "luxon";
import ResponsiveEllipsis from "../../../../partials/content/ResponsiveEllipsis";

function VisitSchedulesReport(props) {
  const { reload, companyProfile } = useGlobalData();
  const [visitSchedules, setVisitSchedules] = useState([]);
  const selectParamOp = useRef(null);
  const [selectParam, setSelectParam] = useState({
    dateFrom: DateTime.fromISO(companyProfile.CreatedAt).startOf("day"),
    dateTo: DateTime.now().endOf("day"),
  });

  useEffect(() => {
    getVisitSchedulesData();
  }, [reload]);

  return (
    <React.Fragment>
      <OverlayPanel
        ref={selectParamOp}
        showCloseIcon
        dismissable={false}
        id="overlay_panel"
        style={{ width: "450px" }}
        className="overlaypanel-demo"
      >
        <div className="row mb-3">
          <div className="col-sm-12 col-md-12 col-lg-12">
            <Card>
              <Card.Header>
                <i className="fas fa-filter"></i>
                <span>
                  <strong>Filter Selection</strong>
                </span>
              </Card.Header>
              <Card.Body>
                <div className="row">
                  <div className="col-6">
                    <KeyboardDatePicker
                      label="Date From"
                      autoOk
                      size="small"
                      inputVariant="outlined"
                      variant="inline"
                      margin="normal"
                      value={selectParam.dateFrom}
                      onChange={(date) => {
                        const selectParamTmp = { ...selectParam };
                        selectParamTmp.dateFrom = date;
                        setSelectParam(selectParamTmp);
                      }}
                      format="yyyy-MM-dd"
                      InputProps={{ readOnly: true }}
                    />
                  </div>
                  <div className="col-6">
                    <KeyboardDatePicker
                      label="Date To"
                      autoOk
                      size="small"
                      inputVariant="outlined"
                      variant="inline"
                      margin="normal"
                      value={selectParam.dateTo}
                      onChange={(date) => {
                        const selectParamTmp = { ...selectParam };
                        selectParamTmp.dateTo = date;
                        setSelectParam(selectParamTmp);
                      }}
                      minDate={selectParam.dateFrom}
                      format="yyyy-MM-dd"
                      InputProps={{ readOnly: true }}
                    />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </div>
        </div>
        <div className="row">
          <div className="col-sm-12 col-md-12 col-lg-12">
            <button
              type="button"
              className="btn btn-success btn-md btn-pill btn-bold float-right"
              onClick={() => {
                getVisitSchedulesData();
              }}
            >
              <i className="fas fa-search"></i>
              Get Visit Schedules Report
            </button>
          </div>
        </div>
      </OverlayPanel>
      {visitSchedules.length ? (
        <React.Fragment>
          <CustomDataTable
            data={visitSchedules}
            onRefreshData={() => {
              getVisitSchedulesData();
            }}
            buttons={[
              {
                name: "Filter",
                onClick: (e) => {
                  selectParamOp.current.toggle(e);
                },
                icon: "fas fa-search",
                color: "info",
              },
            ]}
            columns={[
              {
                header: "Sales Journey Name",
                field: "journeyTitle",
                body: (rowData) => (
                  <React.Fragment>
                    <ResponsiveEllipsis
                      text={rowData.journeyTitle}
                      maxLine="1"
                    />
                  </React.Fragment>
                ),
                sortable: true,
                filter: true,
              },
              {
                header: "Sales Card Name",
                field: "salesCardTitle",
                body: (rowData) => (
                  <React.Fragment>
                    <ResponsiveEllipsis
                      text={rowData.salesCardTitle}
                      maxLine="1"
                    />
                  </React.Fragment>
                ),
                sortable: true,
                filter: true,
              },
              {
                header: "Type",
                field: "customerTypeDesc",
                body: (rowData) => (
                  <React.Fragment>
                    <ResponsiveEllipsis
                      text={rowData.customerTypeDesc}
                      maxLine="1"
                    />
                  </React.Fragment>
                ),
                sortable: true,
                filter: true,
              },
              {
                header: "Name",
                field: "leadName",
                body: (rowData) => (
                  <React.Fragment>
                    {rowData.customerID > 0 ? (
                      <ResponsiveEllipsis
                        text={rowData.customerName}
                        maxLine="1"
                      />
                    ) : (
                      <ResponsiveEllipsis text={rowData.leadName} maxLine="1" />
                    )}
                  </React.Fragment>
                ),
                sortable: true,
                filter: true,
                hidden: true,
              },
              {
                header: "Name",
                field: "customerName",
                body: (rowData) => (
                  <React.Fragment>
                    {rowData.customerID > 0 ? (
                      <ResponsiveEllipsis
                        text={rowData.customerName}
                        maxLine="1"
                      />
                    ) : (
                      <ResponsiveEllipsis text={rowData.leadName} maxLine="1" />
                    )}
                  </React.Fragment>
                ),
                sortable: true,
                filter: true,
              },
              {
                header: "Visit Status",
                field: "statusDesc",
                body: (rowData) => {
                  if (rowData.statusID === 1) {
                    return (
                      <Badge variant="warning">{rowData.statusDesc}</Badge>
                    );
                  } else if (rowData.statusID === 2) {
                    return (
                      <Badge variant="primary">{rowData.statusDesc}</Badge>
                    );
                  } else if (rowData.statusID === 3) {
                    return (
                      <Badge variant="success">{rowData.statusDesc}</Badge>
                    );
                  } else if (rowData.statusID === 9) {
                    return <Badge variant="danger">{rowData.statusDesc}</Badge>;
                  } else {
                    return <Badge variant="dark">{rowData.statusDesc}</Badge>;
                  }
                },
                sortable: true,
                filter: true,
              },
              {
                header: "Plan Visit Date",
                field: "planVisitDate",
                body: (rowData) => formatDate(rowData.planVisitDate),
                sortable: true,
                filter: true,
              },
              {
                header: "Actual Visit Date",
                field: "actVisitDate",
                body: (rowData) =>
                  formatDate(rowData.actVisitDate, true, false),
                sortable: true,
                filter: true,
              },
              {
                header: "Sales Name",
                field: "sales",
                body: (rowData) => (
                  <React.Fragment>
                    <ResponsiveEllipsis text={rowData.sales} maxLine="1" />
                  </React.Fragment>
                ),
                sortable: true,
                filter: true,
              },
            ]}
          />
        </React.Fragment>
      ) : (
        <React.Fragment>
          <Illustration
            imageSource="INVIFO CRM.png"
            description={`You don't have any Visit Schedule for current data selection!`}
            guidance={`Hint: Try to filter another data selection to get some results`}
            descriptionLeftIcon="fas fa-info-circle kt-font-info"
            size="md"
          ></Illustration>
          <div className="text-center py-3">
            <button
              type="button"
              className="btn btn-info btn-md btn-pill btn-bold mx-1"
              onClick={(e) => {
                selectParamOp.current.toggle(e);
              }}
            >
              <i className="fas fa-search"></i>
              Filter
            </button>
          </div>
        </React.Fragment>
      )}
    </React.Fragment>
  );

  async function getVisitSchedulesData() {
    const response = await getVisitSchedulesReport(
      selectParam.dateFrom,
      selectParam.dateTo
    );
    const { data } = response.data;
    setVisitSchedules(data.visitSchedulesReport);
  }
}

export default VisitSchedulesReport;
